import { useEffect, useMemo, useState, type FC, type ReactNode } from 'react';
import { Checkbox, Input, Select, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { IpcAlert, IpcFormField, IpcModal, IpcStatusBadge } from './ui';
import type { Milestone } from '../types';
import { milestoneStatusLabel, terminateReasonOptions, terminateReasonRequiresRemark } from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import {
  defaultTerminateChoice,
  getSpecTerminateNodeStatusOptions,
  hasTerminableSelection,
  isTerminateCellLocked,
  isTerminateSkuColSelected,
  sumTerminateConsumedManDays,
  terminateCellKey,
  type SpecTerminateNodeChoice,
  type TerminateMatrixCell,
  type TerminateMatrixCol,
} from '../utils/terminateMatrix';

type Props = {
  open: boolean;
  milestones: Milestone[];
  cols: TerminateMatrixCol[];
  cells: Record<string, TerminateMatrixCell>;
  confirmLoading?: boolean;
  title?: ReactNode;
  alertTitle?: string;
  /** spec：全部 SKU 不勾选时禁用确认；project：某规格 SKU 全不勾选则跳过该规格 */
  selectionMode?: 'spec' | 'project';
  onCancel: () => void;
  onConfirm: (payload: {
    reason: string;
    remark: string;
    nodeStatuses: {
      specId: number;
      ipdSkuId: number;
      milestoneId: number;
      status: SpecTerminateNodeChoice;
    }[];
  }) => void | Promise<void>;
};

export const TerminateStatusMatrixModal: FC<Props> = ({
  open,
  milestones,
  cols,
  cells,
  confirmLoading,
  title = '终止项目 (批量操作)',
  alertTitle = '终止为不可恢复的终态。将对以下选中的SKU进行终止；当全部规格均已终止时，项目同步为已终止。',
  selectionMode = 'project',
  onCancel,
  onConfirm,
}) => {
  useIpcStatusEnums();
  const nodeStatusOptions = getSpecTerminateNodeStatusOptions();
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [remark, setRemark] = useState('');
  const [choices, setChoices] = useState<Record<string, SpecTerminateNodeChoice>>({});
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const reasonOptions = terminateReasonOptions();
  const remarkRequired = terminateReasonRequiresRemark(reason);
  const skuCols = useMemo(() => cols.filter(c => c.ipdSkuId > 0), [cols]);

  useEffect(() => {
    if (!open) return;
    setReason(undefined);
    setRemark('');
    const next: Record<string, SpecTerminateNodeChoice> = {};
    for (const col of cols) {
      for (const m of milestones) {
        const key = terminateCellKey(col.colKey, m.id);
        const before = cells[key]?.statusBefore ?? 'pending';
        if (!isTerminateCellLocked(before, col.specTerminal)) {
          next[key] = defaultTerminateChoice(before);
        }
      }
    }
    setChoices(next);
    setSelectedKeys(new Set(skuCols.map(c => c.colKey)));
  }, [open, cols, milestones, cells, skuCols]);

  const { consumed, detail: consumedDetail } = useMemo(
    () => sumTerminateConsumedManDays(cols, milestones, cells, choices, selectedKeys),
    [cols, milestones, cells, choices, selectedKeys],
  );

  const statusLabel = (status: string) => milestoneStatusLabel(status);
  const skuSelectedCount = skuCols.filter(c => selectedKeys.has(c.colKey)).length;
  const canSubmitSelection = hasTerminableSelection(cols, selectedKeys);
  const okDisabled = !reason
    || (remarkRequired && !remark.trim())
    || !canSubmitSelection;

  const toggleSku = (col: TerminateMatrixCol) => {
    if (col.ipdSkuId <= 0) return;
    if (col.specTerminal) return;
    const unchecking = selectedKeys.has(col.colKey);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (unchecking) next.delete(col.colKey);
      else next.add(col.colKey);
      return next;
    });
    setChoices(prev => {
      const next = { ...prev };
      for (const m of milestones) {
        const key = terminateCellKey(col.colKey, m.id);
        const before = cells[key]?.statusBefore ?? 'pending';
        if (isTerminateCellLocked(before, col.specTerminal)) continue;
        if (unchecking) {
          next[key] = (before as SpecTerminateNodeChoice) || 'pending';
        } else {
          next[key] = defaultTerminateChoice(before);
        }
      }
      return next;
    });
  };

  return (
    <IpcModal
      open={open}
      title={title}
      className="project-terminate-modal"
      width={Math.min(1080, 320 + Math.max(cols.length, 1) * 120)}
      confirmLoading={confirmLoading}
      okDisabled={okDisabled}
      okText="确认终止"
      cancelText="取消"
      okVariant="destructive"
      onCancel={onCancel}
      onOk={() => {
        if (!reason) return;
        if (terminateReasonRequiresRemark(reason) && !remark.trim()) return;
        if (!hasTerminableSelection(cols, selectedKeys)) return;
        const nodeStatuses: {
          specId: number;
          ipdSkuId: number;
          milestoneId: number;
          status: SpecTerminateNodeChoice;
        }[] = [];
        const includeBySpec = new Map<string, boolean>();
        for (const col of cols) {
          if (col.specTerminal) continue;
          if (!isTerminateSkuColSelected(col, selectedKeys)) continue;
          includeBySpec.set(col.specId, true);
        }
        for (const col of cols) {
          if (col.specTerminal) continue;
          if (!includeBySpec.get(col.specId)) continue;
          if (!isTerminateSkuColSelected(col, selectedKeys)) continue;
          for (const m of milestones) {
            const key = terminateCellKey(col.colKey, m.id);
            const before = cells[key]?.statusBefore ?? 'pending';
            const status = isTerminateCellLocked(before, false)
              ? ((before === 'completed' ? 'completed' : before) as SpecTerminateNodeChoice)
              : (choices[key] ?? defaultTerminateChoice(before));
            const specId = Number(col.specId);
            const milestoneId = Number(m.id);
            if (specId > 0 && milestoneId > 0) {
              nodeStatuses.push({
                specId,
                ipdSkuId: col.ipdSkuId || 0,
                milestoneId,
                status,
              });
            }
          }
        }
        return onConfirm({ reason, remark: remark.trim(), nodeStatuses });
      }}
    >
      <div className="project-terminate-modal__body">
        <IpcAlert tone="warning" title={alertTitle} />

        {skuCols.length > 0 && (
          <div className="terminate-sku-picker">
            <div className="terminate-sku-picker__label">
              <span className="terminate-sku-picker__required">*</span>
              本次终止 SKU
              <Tooltip
                placement="top"
                title="节点状态不一致的 SKU 会拆成独立规格行后再终止。未勾选的 SKU 不终止。"
              >
                <QuestionCircleOutlined className="project-terminate-matrix__tip-icon" />
              </Tooltip>
              <span className="terminate-sku-picker__count">
                已勾选 {skuSelectedCount} / {skuCols.length} 个
              </span>
            </div>
            <div className="terminate-sku-picker__list">
              {skuCols.map(col => {
                const checked = selectedKeys.has(col.colKey);
                const locked = col.specTerminal;
                return (
                  <button
                    key={col.colKey}
                    type="button"
                    className={[
                      'terminate-sku-pill',
                      checked ? 'terminate-sku-pill--checked' : '',
                      locked ? 'terminate-sku-pill--locked' : '',
                    ].filter(Boolean).join(' ')}
                    disabled={locked}
                    title={locked ? '规格已终态，不可取消勾选' : `${col.title} ${col.skuCode ?? ''}`.trim()}
                    onClick={() => toggleSku(col)}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onClick={e => e.preventDefault()}
                    />
                    <span className="terminate-sku-pill__name">{col.title}</span>
                    {col.skuCode ? <span className="terminate-sku-pill__code">{col.skuCode}</span> : null}
                  </button>
                );
              })}
            </div>
            {selectionMode === 'spec' && skuSelectedCount === 0 && (
              <div className="terminate-sku-picker__hint">请至少勾选一个 SKU 后再终止</div>
            )}
          </div>
        )}

        <IpcFormField label="原因" required>
          <Select
            size="small"
            allowClear
            placeholder="请选择终止原因"
            value={reason}
            options={reasonOptions}
            onChange={v => setReason(v)}
            style={{ width: '100%' }}
          />
        </IpcFormField>

        <IpcFormField label="备注" required={remarkRequired}>
          <Input.TextArea
            value={remark}
            onChange={e => setRemark(e.target.value)}
            placeholder={remarkRequired ? '请填写备注' : '选填，可补充说明'}
            rows={2}
            maxLength={1000}
          />
        </IpcFormField>

        <div className="project-terminate-matrix-wrap">
          {milestones.length === 0 || cols.length === 0 ? (
            <div className="spec-pause-modal__empty">暂无流程节点或规格数据</div>
          ) : (
            <table className="project-terminate-matrix">
              <thead>
                <tr>
                  <th className="project-terminate-matrix__col-node">
                    <span className="project-terminate-matrix__node-head">
                      <Tooltip
                        placement="topLeft"
                        overlayStyle={{ maxWidth: 360 }}
                        title={(
                          <div className="project-terminate-matrix__tip">
                            <div>
                              已完成节点锁定「{milestoneStatusLabel('completed')}」；进行中默认「{milestoneStatusLabel('terminated')}」，待开始保持「{milestoneStatusLabel('pending')}」，可下拉改选。自动完成节点不可改为「{milestoneStatusLabel('completed')}」。
                              取消勾选 SKU 时，该列节点恢复为打开弹窗时的原状态，且该列只读；重新勾选后按默认规则可再编辑。
                            </div>
                            <div>
                              多 SKU 规格按 SKU 分列，列头为 SKU 规格名称。
                              人天数仅统计本次将终止的勾选列（已完成/已终止规格不计入）；非跨规格合并节点每 SKU 计基准工时，跨规格合并节点每个规格只计一次。
                            </div>
                          </div>
                        )}
                      >
                        <QuestionCircleOutlined className="project-terminate-matrix__tip-icon" />
                      </Tooltip>
                      流程节点
                    </span>
                  </th>
                  <th className="project-terminate-matrix__col-days">基准工时</th>
                  {cols.map(col => (
                    <th
                      key={col.colKey}
                      title={col.title}
                      className={isTerminateSkuColSelected(col, selectedKeys) ? '' : 'project-terminate-matrix__skipped'}
                    >
                      {col.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {milestones.map(m => {
                  const baseline =
                    cells[terminateCellKey(cols[0]?.colKey ?? '', String(m.id))]?.baselineManDays ?? 0;
                  return (
                    <tr key={m.id}>
                      <td className="project-terminate-matrix__col-node">
                        {m.name}
                        {m.mergeAcrossSpecs ? '（合并不倍增）' : ''}
                      </td>
                      <td className="project-terminate-matrix__col-days">{baseline}</td>
                      {cols.map(col => {
                        const key = terminateCellKey(col.colKey, m.id);
                        const before = cells[key]?.statusBefore ?? 'pending';
                        const selected = isTerminateSkuColSelected(col, selectedKeys);
                        const cellLocked = isTerminateCellLocked(before, col.specTerminal);
                        const cellClass = selected ? '' : 'project-terminate-matrix__skipped';
                        // 未勾选或已锁定：只读
                        if (!selected || cellLocked) {
                          const showStatus = cellLocked
                            ? (before === 'completed' ? 'completed' : before)
                            : (choices[key] ?? defaultTerminateChoice(before));
                          return (
                            <td key={key} className={cellClass}>
                              {showStatus === 'completed' && cellLocked ? (
                                <IpcStatusBadge variant="success" className="spec-terminate-status-badge">
                                  {milestoneStatusLabel('completed')}
                                </IpcStatusBadge>
                              ) : (
                                <span className="project-terminate-matrix__ro">{statusLabel(showStatus)}</span>
                              )}
                            </td>
                          );
                        }
                        const options = cells[key]?.autoCompletion
                          ? nodeStatusOptions.filter(o => o.value !== 'completed')
                          : nodeStatusOptions;
                        return (
                          <td key={key} className={cellClass}>
                            <Select
                              size="small"
                              className="spec-terminate-node-select project-terminate-matrix__select"
                              value={choices[key] ?? defaultTerminateChoice(before)}
                              options={options}
                              onChange={v =>
                                setChoices(prev => ({
                                  ...prev,
                                  [key]: v as SpecTerminateNodeChoice,
                                }))
                              }
                              popupMatchSelectWidth={false}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="spec-pause-modal__summary">
          <div className="spec-pause-modal__summary-main">
            已耗费人天数：
            <strong className="spec-pause-modal__summary-num">{consumed}</strong>
            {' '}人天
          </div>
          <div className="spec-pause-modal__summary-hint">{consumedDetail}</div>
        </div>
      </div>
    </IpcModal>
  );
};
